/**
 * DSettings > "Object Snap" tab.
 *
 * Displays a checkbox for every running-OSNAP mode, bound to the OSMODE
 * sysvar bitmask. AutoCAD parity: the bit→mode mapping is the SAME
 * authoritative table the live snap resolver reads — `OSMODE_BITS` in
 * `apps/web/src/app/scene3d/snap/resolveObjectSnap.ts` — so a checkbox
 * always sets the exact bit the resolver decodes (and which $OSMODE
 * round-trips). All 14 modes are reachable from this tab.
 *
 * OSMODE bit table (AutoCAD 2016+ encoding — Autodesk OSMODE sysvar doc,
 * matches resolver `OSMODE_BITS` exactly):
 *   1     = endpoint            (ENDpoint)
 *   2     = midpoint            (MIDpoint)
 *   4     = center              (CENter)
 *   8     = node                (NODe)
 *   16    = quadrant            (QUAdrant)
 *   32    = intersection        (INTersection)
 *   64    = insertion           (INSertion)
 *   128   = perpendicular       (PERpendicular)
 *   256   = tangent             (TANgent)
 *   512   = nearest             (NEArest)
 *   1024  = geomcenter          (Geometric CEnter)
 *   2048  = appint              (APParent Intersection)
 *   4096  = extension           (EXTension)
 *   8192  = parallel            (PARallel)
 *
 * Bit 16384 is the AutoCAD "OSNAP off" sentinel — NOT a snap mode, so it
 * has no checkbox here (mirrors AutoCAD's Object Snap tab, which lists
 * only the snap modes). `toggleBit` masks individual bits and never
 * touches 16384.
 *
 * Modes that the resolver decodes but cannot yet resolve to a usable
 * candidate (`OSNAP_MODES_PENDING_IMPLEMENTATION` in resolveObjectSnap.ts
 * — after OSNAP-INTERSECT-EMIT un-pended intersection + appint, only
 * `parallel` remains) are rendered in their correct AutoCAD position,
 * dimmed, with a "pending" tooltip — and cannot be newly ENABLED (that
 * would offer a snap that silently does nothing). Because this tab is the
 * OSMODE editor, an already-set pending bit (e.g. a value from a loaded
 * DXF / an older dialog) stays CLEARABLE so the bit is never trapped: a
 * pending checkbox is disabled only while its bit is currently off. Each
 * mode becomes fully live automatically once its arm removes it from that
 * set.
 */

import { useT } from "../../i18n";
import {
  OSMODE_BITS,
  OSNAP_MODES_PENDING_IMPLEMENTATION,
} from "../../scene3d/snap/resolveObjectSnap";
import { useSysvarStore } from "../sysvarStore";

// OTRACK-SUBSYSTEM — real AutoCAD's Object Snap tab has TWO master
// checkboxes above the mode grid: "Object Snap On (F3)" and "Object Snap
// Tracking On (F11)". This app's F3/OSMODE master toggle is not modelled as
// a dedicated checkbox here yet (out of THIS slice's scope — the mode grid
// below already lets every bit clear to 0), but OTRACK's own toggle is
// exactly this slice's CLI/UI deliverable, so it is added now.

/** bit → resolver OsnapMode (the authoritative `OSMODE_BITS` table). */
const MODE_BY_BIT = new Map(OSMODE_BITS.map(({ bit, mode }) => [bit, mode]));

type OSnapEntry = {
  bit: number;
  labelKey:
    | "dsettings.osnap.endpoint"
    | "dsettings.osnap.midpoint"
    | "dsettings.osnap.center"
    | "dsettings.osnap.node"
    | "dsettings.osnap.quadrant"
    | "dsettings.osnap.intersection"
    | "dsettings.osnap.insertion"
    | "dsettings.osnap.perpendicular"
    | "dsettings.osnap.tangent"
    | "dsettings.osnap.nearest"
    | "dsettings.osnap.geomcenter"
    | "dsettings.osnap.apparent"
    | "dsettings.osnap.extension"
    | "dsettings.osnap.parallel";
};

/**
 * Bit→label table. Bits and order MUST mirror the resolver's
 * `OSMODE_BITS` (resolveObjectSnap.ts) so each checkbox writes the bit
 * the live snap resolver decodes. Exported for the bit-parity +
 * completeness coverage tests.
 */
export const OSNAP_ENTRIES: OSnapEntry[] = [
  { bit: 1, labelKey: "dsettings.osnap.endpoint" },
  { bit: 2, labelKey: "dsettings.osnap.midpoint" },
  { bit: 4, labelKey: "dsettings.osnap.center" },
  { bit: 8, labelKey: "dsettings.osnap.node" },
  { bit: 16, labelKey: "dsettings.osnap.quadrant" },
  { bit: 32, labelKey: "dsettings.osnap.intersection" },
  { bit: 64, labelKey: "dsettings.osnap.insertion" },
  { bit: 128, labelKey: "dsettings.osnap.perpendicular" },
  { bit: 256, labelKey: "dsettings.osnap.tangent" },
  { bit: 512, labelKey: "dsettings.osnap.nearest" },
  { bit: 1024, labelKey: "dsettings.osnap.geomcenter" },
  { bit: 2048, labelKey: "dsettings.osnap.apparent" },
  { bit: 4096, labelKey: "dsettings.osnap.extension" },
  { bit: 8192, labelKey: "dsettings.osnap.parallel" },
];

export function OSnapTab() {
  const t = useT();
  const osMode = useSysvarStore((s) => s.values.OSMODE);
  const otrackMode = useSysvarStore((s) => s.values.OTRACKMODE);
  const setVar = useSysvarStore((s) => s.setVar);

  function toggleBit(bit: number, checked: boolean) {
    const next = checked ? osMode | bit : osMode & ~bit;
    setVar("OSMODE", next);
  }

  return (
    <div className="dsettings-tab-content">
      <div className="dsettings-section">
        <label className="dsettings-checkbox-row">
          <input
            type="checkbox"
            data-testid="otrack-enable-checkbox"
            checked={otrackMode !== 0}
            onChange={(e) => setVar("OTRACKMODE", e.target.checked ? 1 : 0)}
          />
          <span className="dsettings-checkbox-label">
            {t("dsettings.osnap.otrackEnable")}
          </span>
        </label>
        <div className="dsettings-section-title">
          {t("dsettings.osnap.title")}
        </div>
        <div className="dsettings-osnap-grid">
          {OSNAP_ENTRIES.map(({ bit, labelKey }) => {
            const mode = MODE_BY_BIT.get(bit);
            const pending = mode
              ? OSNAP_MODES_PENDING_IMPLEMENTATION.has(mode)
              : false;
            const checked = (osMode & bit) !== 0;
            // A pending mode (no usable candidate yet) must never be newly
            // ENABLED from the UI — that would offer a snap that silently does
            // nothing. But the Object-Snap tab is the OSMODE editor, so an
            // already-set pending bit (e.g. an already-set parallel bit (8192)
            // from a loaded DXF / an older dialog) must still be CLEARABLE —
            // otherwise the bit is trapped. So: disable only when the pending
            // bit is currently OFF.
            const lockedOff = pending && !checked;
            const pendingTitle = pending
              ? t("dsettings.osnap.pending")
              : undefined;
            const className =
              "dsettings-checkbox-row" +
              (pending ? " dsettings-checkbox-row--pending" : "") +
              (lockedOff ? " dsettings-checkbox-row--locked" : "");
            return (
              <label key={bit} className={className} title={pendingTitle}>
                <input
                  type="checkbox"
                  data-testid={`osnap-checkbox-${labelKey.split(".").pop()}`}
                  checked={checked}
                  disabled={lockedOff}
                  aria-disabled={lockedOff || undefined}
                  onChange={(e) => {
                    // block ENABLING a pending mode; allow clearing it
                    if (pending && e.target.checked) return;
                    toggleBit(bit, e.target.checked);
                  }}
                />
                <span className="dsettings-checkbox-label">{t(labelKey)}</span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}
